import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { ExamSurfaceDisclaimer } from '@/components/ExamSurfaceDisclaimer';
import { Alert } from '@/components/ui/Alert';
import { LoadingLine } from '@/components/ui/States';
import { StaffOnly } from '@/features/admin-content/StaffOnly';
import {
  AdminApiError,
  fetchGeneration,
  importGeneration,
  submitGenerationReview,
} from '@/features/admin-content/admin-api';
import { GenerationDetailView } from '@/features/admin-content/GenerationDetailView';
import type { ReviewDecision } from '@/features/admin-content/types';
import { useCan, useCurrentUser } from '@/features/admin-content/use-current-user';
import { useGenerationEvents } from '@/features/admin-content/use-generation-events';
import { BRAND } from '@/lib/brand';
import { authClient } from '@/lib/auth-client';

export const Route = createFileRoute('/admin/content/$generationId')({
  component: AdminContentDetailPage,
});

function AdminContentDetailPage() {
  const { generationId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: session, isPending: sessionPending } = authClient.useSession();
  const meQuery = useCurrentUser();
  const canReview = useCan('content.admin');
  const [reviewPendingKey, setReviewPendingKey] = useState<string | null>(null);

  const detailQuery = useQuery({
    queryKey: ['admin', 'generation', generationId],
    enabled: canReview,
    queryFn: () => fetchGeneration(generationId),
  });

  const refreshDetail = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'generation', generationId] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'generations'] });
  }, [queryClient, generationId]);

  const progress = useGenerationEvents(
    generationId,
    detailQuery.data?.status === 'queued' || detailQuery.data?.status === 'running',
    refreshDetail,
  );

  const reviewMutation = useMutation({
    mutationFn: (input: {
      slug: string;
      ref: string | null;
      decision: ReviewDecision;
      notes?: string;
    }) => submitGenerationReview(generationId, input),
    onMutate: (input) => {
      setReviewPendingKey(`${input.slug}:${input.ref ?? ''}`);
    },
    onSettled: () => {
      setReviewPendingKey(null);
      refreshDetail();
    },
  });

  const importMutation = useMutation({
    mutationFn: () => importGeneration(generationId),
    onSuccess: refreshDetail,
  });

  if (sessionPending || meQuery.isPending) {
    return <LoadingLine>Loading</LoadingLine>;
  }

  if (!session) {
    void navigate({ to: '/sign-in' });
    return null;
  }

  if (!canReview) {
    return <StaffOnly />;
  }

  if (detailQuery.isPending) {
    return <LoadingLine>Loading the run</LoadingLine>;
  }

  if (detailQuery.isError || !detailQuery.data || !meQuery.data) {
    return (
      <Alert
        tone="danger"
        title={
          detailQuery.error instanceof AdminApiError
            ? 'We could not find that generation run.'
            : 'Something went wrong.'
        }
      />
    );
  }

  return (
    <div className="grid gap-6">
      <ExamSurfaceDisclaimer text={BRAND.disclaimer} />
      <GenerationDetailView
        detail={detailQuery.data}
        currentUserId={meQuery.data.id}
        progressStep={progress?.step ?? null}
        progressSlug={progress?.slug ?? null}
        onReview={(input) => {
          reviewMutation.mutate(input);
        }}
        onImport={() => {
          importMutation.mutate();
        }}
        importPending={importMutation.isPending}
        reviewPendingKey={reviewPendingKey}
      />
    </div>
  );
}
